<template>
  <div class="benefit-selector-container">
    <div v-if="loading" class="tw-flex tw-items-center tw-gap-3 tw-py-6 tw-justify-center">
      <v-progress-circular indeterminate color="#00AEEF" size="22" width="2"></v-progress-circular>
      <span class="tw-text-sm tw-text-gray-400">Loading available benefits…</span>
    </div>

    <div v-else-if="compact && filteredBenefits.length > 0" class="ben-compact">
      <section v-if="freeCoverBenefits.length" class="ben-group">
        <p class="ben-kicker">Free with comprehensive cover</p>
        <div
          v-for="benefit in freeCoverBenefits"
          :key="`free-${benefit.sectCode}`"
          class="ben-row ben-row--free"
        >
          <div class="ben-free-main">
            <span class="ben-free-icon"><v-icon small color="#059669">mdi-shield-check</v-icon></span>
            <span class="ben-row-copy">
              <span class="bex-name">{{ getDisplayName(benefit) }}</span>
              <span class="ben-row-note">{{ freeCoverNote(benefit) }}</span>
            </span>
            <span
              v-if="freeCoverExtra(benefit)"
              class="ben-row-price"
              :class="{ 'ben-row-price--saving': freeCoverExtra(benefit) < 0 }"
            >{{ signedAmount(freeCoverExtra(benefit)) }}</span>
            <span v-else class="bex-free-tag">{{ freeCoverTag(benefit) }}</span>
          </div>
          <button
            v-if="!isLimitOpen(benefit)"
            type="button"
            class="ben-link"
            @click="openLimit(benefit)"
          >Change limit</button>
          <div v-else-if="vehicleMode" class="ben-cars">
            <div v-for="vehicle in vehicles" :key="vehicle.key" class="ben-car">
              <div class="ben-car-limit ben-car-limit--free">
                <span class="ben-car-label">{{ vehicle.label }}</span>
                <div class="bex-extra-row">
                  <span class="bex-extra-prefix">Ksh</span>
                  <input
                    class="bex-extra-input"
                    type="text"
                    :aria-label="`${getDisplayName(benefit)} limit for ${vehicle.label}`"
                    :placeholder="formatAmount(vehicleMinLimit(benefit, vehicle))"
                    :value="vehicleLimitDisplay(benefit, vehicle)"
                    @input="handleVehicleLimitInput($event, benefit, vehicle)"
                    @blur="handleVehicleLimitBlur(benefit, vehicle)"
                  />
                </div>
                <span
                  class="ben-row-price"
                  :class="{ 'ben-row-price--saving': vehicleChange(benefit, vehicle) < 0 }"
                >{{ signedAmount(vehicleChange(benefit, vehicle)) }}</span>
              </div>
              <p v-if="isVehicleBelowNoticeLimit(benefit, vehicle)" class="ben-limit-hint">
                Enter Ksh {{ formatAmount(vehicleMinLimit(benefit, vehicle)) }} or more
              </p>
            </div>
            <p class="ben-free-help">{{ freeLimitHelp(benefit) }}</p>
          </div>
          <div v-else class="ben-limit">
            <label class="ben-limit-lbl">Cover limit</label>
            <div class="bex-extra-row">
              <span class="bex-extra-prefix">Ksh</span>
              <input
                class="bex-extra-input"
                type="text"
                :aria-label="`${getDisplayName(benefit)} limit`"
                :placeholder="formatAmount(minLimit(benefit))"
                :value="getCustomLimitDisplay(benefit)"
                @input="handleLimitInput($event, benefit)"
                @blur="handleLimitBlur(benefit)"
              />
            </div>
            <p v-if="isBelowNoticeLimit(benefit)" class="ben-limit-hint">
              Enter Ksh {{ formatAmount(minLimit(benefit)) }} or more
            </p>
            <p v-else class="ben-free-help">{{ freeLimitHelp(benefit) }}</p>
          </div>
        </div>
      </section>

      <section v-if="selectedBenefits.length" class="ben-group">
        <p class="ben-kicker">Extras on this renewal</p>
        <div
          v-for="benefit in selectedBenefits"
          :key="`on-${benefit.sectCode}`"
          class="ben-row ben-row--on"
        >
          <button
            type="button"
            class="ben-row-main"
            :class="{ 'ben-row-main--locked': isLockedNoticeBenefit(benefit) }"
            :title="isLockedNoticeBenefit(benefit) ? 'Comes with this cover' : ''"
            @click="toggleBenefit(benefit)"
          >
            <span v-if="isLockedNoticeBenefit(benefit)" class="bex-chk bex-chk-on bex-chk-locked">
              <v-icon x-small color="white">mdi-lock</v-icon>
            </span>
            <span v-else class="bex-chk bex-chk-on">
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                <path d="M2 6L5 9L10 3" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <span class="ben-row-copy">
              <span class="bex-name">{{ getDisplayName(benefit) }}</span>
              <span class="ben-row-note">{{ compactStatus(benefit) }}</span>
            </span>
            <span
              class="ben-row-price"
              :class="{ 'ben-row-price--saving': noticeLimitChange(benefit) < 0 }"
            >{{ compactPrice(benefit) }}</span>
          </button>
          <div v-if="vehicleMode" class="ben-cars" @click.stop>
            <div v-for="vehicle in vehicles" :key="vehicle.key" class="ben-car">
              <button
                type="button"
                class="ben-car-toggle"
                :class="{ 'ben-car-toggle--locked': isVehicleLocked(benefit, vehicle) }"
                @click="toggleVehicle(benefit, vehicle)"
              >
                <span v-if="isVehicleLocked(benefit, vehicle)" class="bex-chk bex-chk-on bex-chk-locked">
                  <v-icon x-small color="white">mdi-lock</v-icon>
                </span>
                <span v-else class="bex-chk" :class="{ 'bex-chk-on': vehicleHas(benefit, vehicle) }">
                  <svg v-if="vehicleHas(benefit, vehicle)" width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6L5 9L10 3" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
                <span class="ben-car-label">{{ vehicle.label }}</span>
                <span
                  class="ben-row-price"
                  :class="{ 'ben-row-price--saving': vehicleLimitChange(benefit, vehicle) < 0 }"
                >{{ vehiclePrice(benefit, vehicle) }}</span>
              </button>
              <div v-if="vehicleHas(benefit, vehicle) && canHaveCustomLimit(benefit)" class="ben-car-limit">
                <div class="bex-extra-row">
                  <span class="bex-extra-prefix">Ksh</span>
                  <input
                    class="bex-extra-input"
                    type="text"
                    :aria-label="`${getDisplayName(benefit)} limit for ${vehicle.label}`"
                    :placeholder="vehicleLimitPlaceholder(benefit, vehicle)"
                    :value="vehicleLimitDisplay(benefit, vehicle)"
                    @input="handleVehicleLimitInput($event, benefit, vehicle)"
                    @blur="handleVehicleLimitBlur(benefit, vehicle)"
                  />
                </div>
                <p v-if="isVehicleBelowNoticeLimit(benefit, vehicle)" class="ben-limit-hint">
                  Enter Ksh {{ formatAmount(vehicleSection(benefit, vehicle).limit) }} or more
                </p>
              </div>
            </div>
          </div>
          <div
            v-else-if="canHaveCustomLimit(benefit)"
            class="ben-limit"
            @click.stop
          >
            <label class="ben-limit-lbl">{{ lockedSection(benefit) ? 'Cover limit' : 'Custom limit' }}</label>
            <div class="bex-extra-row">
              <span class="bex-extra-prefix">Ksh</span>
              <input
                class="bex-extra-input"
                type="text"
                :placeholder="limitPlaceholder(benefit)"
                :value="getCustomLimitDisplay(benefit)"
                @input="handleLimitInput($event, benefit)"
                @blur="handleLimitBlur(benefit)"
              />
            </div>
            <p v-if="isBelowNoticeLimit(benefit)" class="ben-limit-hint">
              Enter Ksh {{ formatAmount(noticeSection(benefit).limit) }} or more
            </p>
          </div>
        </div>
      </section>

      <section v-if="availableBenefits.length" class="ben-group">
        <p class="ben-kicker">Optional extras · tick to add</p>
        <button
          v-for="benefit in availableBenefits"
          :key="`off-${benefit.sectCode}`"
          type="button"
          class="ben-off"
          @click="toggleBenefit(benefit)"
        >
          <span class="bex-chk"></span>
          <span class="ben-row-copy">
            <span class="bex-name">{{ getDisplayName(benefit) }}</span>
            <span class="ben-row-note">
              {{ noticeSection(benefit) ? 'Was on this policy · tick to keep it' : getBenefitPricingLabel(benefit) }}
            </span>
          </span>
          <span class="ben-row-price ben-row-price--muted">{{ compactPrice(benefit) }}</span>
        </button>
      </section>
    </div>

    <div v-else-if="filteredBenefits.length > 0" class="benefit-expand-list">
      <section v-if="freeCoverBenefits.length" class="free-cover">
        <div class="free-cover-head">
          <v-icon small color="#059669">mdi-shield-check</v-icon>
          <span>Free with comprehensive cover</span>
        </div>
        <p class="free-cover-hint">Already included at no cost. Raise a limit only if the client wants more cover.</p>
        <div
          v-for="benefit in freeCoverBenefits"
          :key="`free-${benefit.sectCode}`"
          class="free-row"
        >
          <div class="free-row-main">
            <span class="ben-row-copy">
              <span class="bex-name">{{ getDisplayName(benefit) }}</span>
              <span class="ben-row-note">{{ freeCoverNote(benefit) }}</span>
            </span>
            <span v-if="freeCoverExtra(benefit)" class="bex-price bex-price-active">
              <template v-if="updating">
                <v-progress-circular indeterminate color="#00AEEF" size="10" width="1.5" class="tw-mr-1"></v-progress-circular>
              </template>
              <template v-else>+ Ksh {{ formatAmount(freeCoverExtra(benefit)) }}</template>
            </span>
            <span v-else class="bex-free-tag">Free</span>
          </div>
          <button
            v-if="!isLimitOpen(benefit)"
            type="button"
            class="ben-link ben-link--flush"
            @click="openLimit(benefit)"
          >Increase limit</button>
          <div v-else class="free-row-edit">
            <label class="bex-extra-lbl">New limit (above Ksh {{ formatAmount(benefit.freeLimit) }})</label>
            <div class="bex-extra-row">
              <span class="bex-extra-prefix">Ksh</span>
              <input
                class="bex-extra-input"
                type="text"
                :aria-label="`${getDisplayName(benefit)} limit`"
                :placeholder="formatAmount(benefit.freeLimit)"
                :value="getCustomLimitDisplay(benefit)"
                @input="handleFreeLimitInput($event, benefit)"
                @blur="handleFreeLimitBlur(benefit)"
              />
            </div>
            <p class="ben-free-help">{{ freeLimitHelp(benefit) }}</p>
            <button type="button" class="ben-link ben-link--flush" @click="resetFreeLimit(benefit)">
              Keep the free limit
            </button>
          </div>
        </div>
      </section>

      <p v-if="freeCoverBenefits.length && extraBenefits.length" class="ben-kicker tw-mt-2 tw-mb-0">
        Optional extras · tick to add
      </p>
      <div
        v-for="benefit in extraBenefits"
        :key="benefit.sectCode"
        :class="['bex-card', isSelected(benefit) ? 'bex-selected' : '']"
      >
        <!-- Card header row: checkbox + name + pricing label + total price -->
        <div class="bex-head" @click="toggleBenefit(benefit)">
          <div class="bex-chk" :class="{ 'bex-chk-on': isSelected(benefit) }">
            <svg v-if="isSelected(benefit)" width="10" height="10" viewBox="0 0 12 12" fill="none">
              <path d="M2 6L5 9L10 3" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="bex-title-wrap">
            <span class="bex-name">{{ getDisplayName(benefit) }}</span>
            <span class="bex-pricing-badge">
              {{ getBenefitPricingLabel(benefit) }}
            </span>
          </div>
          <!-- Price badge: show FREE when freeLimit benefit with no custom set -->
          <span v-if="isFreeBenefit(benefit)" class="bex-free-tag">
            <v-icon x-small color="#059669" class="tw-mr-0.5">mdi-check-circle-outline</v-icon>
            Free
          </span>
          <span v-else class="bex-price" :class="{ 'bex-price-active': isSelected(benefit) }">
            <template v-if="updating && isSelected(benefit)">
              <v-progress-circular indeterminate color="#00AEEF" size="10" width="1.5" class="tw-mr-1"></v-progress-circular>
            </template>
            <template v-else>
              + Ksh {{ formatAmount(getFinalPremiumIncludingSurcharge(benefit)) }}
            </template>
          </span>
        </div>

        <!-- Expanded detail: shown only when benefit is selected -->
        <div v-if="isSelected(benefit)" class="bex-body">
          <div class="bex-detail-grid">

            <!-- FREE notice (freeLimit benefit, no custom set) -->
            <div v-if="benefit.freeLimit > 0 && !customLimits[benefit.sectCode]" class="bex-free-notice">
              <v-icon x-small color="#059669" class="tw-mr-1">mdi-check-circle-outline</v-icon>
              Covered free up to <strong>Ksh {{ formatAmount(benefit.freeLimit) }}</strong>.
              Enter a custom limit below to increase coverage.
            </div>

            <!-- Base Premium row (only show when there's a charge) -->
            <div v-if="getFinalPremium(benefit) > 0" class="bex-row">
              <span class="bex-lbl">Benefit Premium (incl. taxes)</span>
              <span class="bex-val">Ksh {{ formatAmount(getFinalPremium(benefit)) }}</span>
            </div>

            <!-- Calculation row -->
            <div v-if="benefit.divFactor > 1" class="bex-row">
              <span class="bex-lbl">Calculation</span>
              <span class="bex-val">
                {{ benefit.rate }}% of
                {{ benefit.freeLimit > 0 ? '(Custom Limit − Free Limit)' : 'Vehicle Value' }}
              </span>
            </div>
            <div v-else class="bex-row">
              <span class="bex-lbl">Pricing Type</span>
              <span class="bex-val">Fixed annual charge</span>
            </div>

            <!-- Free Limit row -->
            <div v-if="benefit.freeLimit > 0" class="bex-row">
              <span class="bex-lbl tw-text-green-600">
                <v-icon x-small color="#059669" class="tw-mr-1">mdi-check-circle-outline</v-icon>
                Free Coverage Limit
              </span>
              <span class="bex-val tw-text-green-600">Ksh {{ formatAmount(benefit.freeLimit) }}</span>
            </div>

            <!-- Custom limit chargeable breakdown (only when custom > freeLimit) -->
            <template v-if="benefit.freeLimit > 0 && customLimits[benefit.sectCode] > benefit.freeLimit">
              <div class="bex-row">
                <span class="bex-lbl">Custom Limit Set</span>
                <span class="bex-val">Ksh {{ formatAmount(customLimits[benefit.sectCode]) }}</span>
              </div>
              <div class="bex-row tw-border-t tw-border-dashed tw-border-gray-200 tw-pt-2">
                <span class="bex-lbl tw-font-semibold">Chargeable Amount</span>
                <span class="bex-val tw-font-semibold">
                  Ksh {{ formatAmount(customLimits[benefit.sectCode] - benefit.freeLimit) }}
                </span>
              </div>
            </template>

            <!-- Vehicle value row (for non-freeLimit percentage benefits) -->
            <div v-if="carValue && benefit.divFactor > 1 && !benefit.freeLimit" class="bex-row">
              <span class="bex-lbl">Vehicle Value</span>
              <span class="bex-val">Ksh {{ formatAmount(carValue) }}</span>
            </div>

            <!-- Custom Limit input section -->
            <div v-if="canHaveCustomLimit(benefit)" class="bex-extra-wrap tw-mt-2">
              <label class="bex-extra-lbl">
                {{ benefit.freeLimit > 0
                  ? `Increase coverage above free limit (Ksh ${formatAmount(benefit.freeLimit)})`
                  : 'Set a custom coverage limit' }}
              </label>
              <div class="bex-extra-row">
                <span class="bex-extra-prefix">Ksh</span>
                <input
                  class="bex-extra-input"
                  type="text"
                  :placeholder="benefit.freeLimit
                    ? `Min Ksh ${formatAmount(benefit.freeLimit)}`
                    : 'Enter limit amount'"
                  :value="getCustomLimitDisplay(benefit)"
                  @input="handleLimitInput($event, benefit)"
                />
              </div>
              <p v-if="benefit.freeLimit > 0 && !customLimits[benefit.sectCode]"
                 class="tw-text-[10px] tw-text-gray-400 tw-mt-1">
                Leave blank for free coverage up to Ksh {{ formatAmount(benefit.freeLimit) }}
              </p>
            </div>

            <!-- Total row -->
            <div v-if="getFinalPremiumIncludingSurcharge(benefit) > 0" class="bex-row bex-total-row">
              <span class="bex-lbl">Total Annual Benefit Premium</span>
              <span class="bex-val underline-premium">
                Ksh {{ formatAmount(getFinalPremiumIncludingSurcharge(benefit)) }}
              </span>
            </div>
            <div v-else-if="benefit.freeLimit > 0" class="bex-row bex-total-row">
              <span class="bex-lbl">Total Annual Benefit Premium</span>
              <span class="bex-val tw-text-green-600 tw-font-bold">Free</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary strip -->
    <div v-if="!compact && addedCount > 0 && !updating" class="benefit-summary-strip tw-mt-4">
      <v-icon x-small color="#00AEEF" class="tw-mr-1">mdi-check-circle</v-icon>
      <span>{{ addedCount }} benefit{{ addedCount > 1 ? 's' : '' }} added</span>
      <span class="tw-ml-auto tw-font-bold tw-text-[#1E4276]">
        + Ksh {{ formatAmount(totalPremium) }}
      </span>
    </div>

    <div v-if="!loading && !filteredBenefits.length" class="tw-py-6 tw-text-center tw-text-gray-400 tw-text-sm">
      No additional benefits available for this cover.
    </div>
  </div>
</template>

<script>
import { isFreeCoverBenefit, renewalLimitChangePremium } from '@/utils/motorBenefits';

const UPDATE_LIMITS_EVENT = 'update:customLimits';

export default {
  name: 'BenefitLimitSelector',
  props: {
    benefits: { type: Array, default: () => [] },
    selectedAddons: { type: Array, default: () => [] },
    compact: { type: Boolean, default: false },
    customLimits: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    updating: { type: Boolean, default: false },
    carValue: { type: [Number, String], default: 0 },
    premiumLookup: { type: Object, default: null },
    basicPremium: { type: Number, default: 0 },
    // Renewals: benefits underwritten on the GIS notice, keyed by sect code (see renewalNoticeSections).
    noticeSections: { type: Object, default: null },
    // Multi-vehicle renewals (compact only): [{ key, label, value, noticeSections }] with a choice per vehicle key.
    vehicles: { type: Array, default: () => [] },
    vehicleSelections: { type: Object, default: () => ({}) },
  },
  data() {
    return { openLimits: {} };
  },
  computed: {
    vehicleMode() {
      return this.compact && this.vehicles.length > 1;
    },
    filteredBenefits() {
      // Hide benefits that contain 'AA' or 'RESCUE'
      const EXCLUDED_KEYWORDS = ['AA', 'RESCUE'];
      return (this.benefits || []).filter((b) => {
        const desc = (b.sectDesc || '').toUpperCase();
        return !EXCLUDED_KEYWORDS.some((kw) => desc.includes(kw));
      });
    },
    totalPremium() {
      return this.benefits
        .filter((b) => this.selectedAddons.includes(b.sectCode))
        .reduce((sum, b) => sum + this.getFinalPremiumIncludingSurcharge(b), 0);
    },
    freeCoverBenefits() {
      return this.filteredBenefits.filter((b) => this.isFreeCover(b));
    },
    extraBenefits() {
      return this.filteredBenefits.filter((b) => !this.isFreeCover(b));
    },
    selectedBenefits() {
      return this.extraBenefits.filter((b) => this.isSelected(b));
    },
    availableBenefits() {
      return this.extraBenefits.filter((b) => !this.isSelected(b));
    },
    addedCount() {
      return this.extraBenefits.filter((b) => this.isSelected(b)).length
        + this.freeCoverBenefits.filter((b) => this.freeCoverExtra(b) > 0).length;
    },
  },
  methods: {
    isSelected(benefit) {
      if (this.vehicleMode) return this.vehicles.some((vehicle) => this.vehicleHas(benefit, vehicle));
      return this.selectedAddons.includes(benefit.sectCode);
    },

    // ── Returns true when benefit is "free" (freeLimit > 0, no custom limit above it) ──
    isFreeBenefit(benefit) {
      if (!benefit.freeLimit || benefit.freeLimit <= 0) return false;
      const custom = this.customLimits[benefit.sectCode];
      return !custom || custom <= benefit.freeLimit;
    },

    isFreeCover(benefit) {
      return isFreeCoverBenefit(benefit);
    },

    isLimitOpen(benefit) {
      return !!this.openLimits[benefit.sectCode] || this.freeCoverExtra(benefit) > 0;
    },

    openLimit(benefit) {
      this.$set(this.openLimits, benefit.sectCode, true);
    },

    // Premium on top of the free cover: a raised limit, or on renewals the change to the notice premium.
    freeCoverExtra(benefit) {
      if (this.vehicleMode || this.noticeSection(benefit)) return this.noticeLimitChange(benefit);
      return this.isSelected(benefit) ? this.getFinalPremium(benefit) : 0;
    },

    coveredLimit(benefit) {
      return Math.max(Number(this.customLimits[benefit.sectCode]) || 0, this.minLimit(benefit));
    },

    freeCoverNote(benefit) {
      if (this.vehicleMode) {
        const limits = this.vehicles.map((vehicle) => Math.max(
          this.vehicleLimit(benefit, vehicle),
          this.vehicleMinLimit(benefit, vehicle),
        ));
        if (limits.every((limit) => limit === limits[0])) {
          return `Covered up to Ksh ${this.formatAmount(limits[0])} on every vehicle`;
        }
        return this.vehicles
          .map((vehicle, i) => `${vehicle.label} up to Ksh ${this.formatAmount(limits[i])}`)
          .join(' · ');
      }
      const covered = `Covered up to Ksh ${this.formatAmount(this.coveredLimit(benefit))}`;
      return this.compact && !this.noticeSection(benefit) ? `${covered} · added free on this renewal` : covered;
    },

    freeCoverTag(benefit) {
      const sections = this.vehicleMode
        ? this.vehicles.map((vehicle) => this.vehicleSection(benefit, vehicle))
        : [this.noticeSection(benefit)];
      return sections.some((section) => section && section.premium > 0) ? 'Included' : 'Free';
    },

    freeLimitHelp(benefit) {
      const pct = benefit.divFactor > 0 ? (Number(benefit.rate) / benefit.divFactor) * 100 : 0;
      const charge = pct > 0 ? ` at ${Number(pct.toFixed(2))}% of the amount above it` : '';
      return `The first Ksh ${this.formatAmount(benefit.freeLimit)} is free. A higher limit is charged${charge}.`;
    },

    minLimit(benefit) {
      const section = this.lockedSection(benefit);
      if (section) return section.limit;
      return this.isFreeCover(benefit) ? Number(benefit.freeLimit) : 0;
    },

    vehicleMinLimit(benefit, vehicle) {
      const section = this.vehicleLockedSection(benefit, vehicle);
      if (section) return section.limit;
      return this.isFreeCover(benefit) ? Number(benefit.freeLimit) : 0;
    },

    handleFreeLimitInput(event, benefit) {
      const input = event.target;
      const num = parseInt(input.value.replace(/[^0-9]/g, ''), 10) || 0;
      input.value = num ? num.toLocaleString() : '';
      const wasCharged = this.freeCoverExtra(benefit) > 0;
      const code = benefit.sectCode;
      const limits = { ...this.customLimits };
      if (num > 0) limits[code] = num;
      else delete limits[code];
      const has = this.selectedAddons.some((c) => String(c) === String(code));
      let selected = this.selectedAddons.filter((c) => String(c) !== String(code));
      if (num > 0) selected = has ? [...this.selectedAddons] : [...this.selectedAddons, code];
      this.emitFreeLimit(selected, limits, num <= benefit.freeLimit && !wasCharged);
    },

    handleFreeLimitBlur(benefit) {
      if ((Number(this.customLimits[benefit.sectCode]) || 0) > Number(benefit.freeLimit)) return;
      this.resetFreeLimit(benefit);
    },

    resetFreeLimit(benefit) {
      const wasCharged = this.freeCoverExtra(benefit) > 0;
      const limits = { ...this.customLimits };
      delete limits[benefit.sectCode];
      this.$set(this.openLimits, benefit.sectCode, false);
      const selected = this.selectedAddons.filter((c) => String(c) !== String(benefit.sectCode));
      this.emitFreeLimit(selected, limits, !wasCharged);
    },

    emitFreeLimit(selected, limits, skipApiSync) {
      this.$emit('update:selectedAddons', selected);
      this.$emit(UPDATE_LIMITS_EVENT, limits);
      this.$emit('change', { selected, limits, skipApiSync });
    },

    // ── Normalized display name (Radio Cassette → Infortainment) ──
    getDisplayName(benefit) {
      const raw = (benefit.sectDesc || '').toUpperCase().trim();
      if (raw === 'INFORTAINMENT' || raw.includes('RADIO CASSETTE') || raw.includes('RADIO-CASSETTE') || raw.includes('ENTERTAINMENT')) {
        return 'Infotainment (Radio/Entertainment Unit)';
      }
      if (raw.includes('PERSONAL ACCIDENT') || (raw.includes('PA') && (raw.includes('BENEFIT') || raw.includes('COVER')))) {
        return `${benefit.sectDesc || ''} (Driver only)`;
      }
      const d = benefit.sectDesc || '';
      return d.charAt(0).toUpperCase() + d.slice(1).toLowerCase();
    },

    isPVTBenefit(benefit) {
      const raw = (benefit.sectDesc || '').toUpperCase().trim();
      return raw.includes('PVT') || raw.includes('POLITICAL VIOLENCE') || raw.includes('TERRORISM');
    },

    isExcessBenefit(benefit) {
      const raw = (benefit.sectDesc || '').toUpperCase().trim();
      return raw.includes('EXCESS PROTECTOR') || raw.includes('EXCESS PROTECT') || raw.includes('E.P.P') || raw.includes('EPP');
    },

    canHaveCustomLimit(benefit) {
      if (this.isExcessBenefit(benefit)) return false;
      if (this.isPVTBenefit(benefit)) return false;

      // All benefits with a freeLimit or percentage rate can have custom limits
      return benefit.freeLimit > 0 || benefit.divFactor > 1;
    },

    getBenefitPricingLabel(benefit) {
      const isPVT = this.isPVTBenefit(benefit);
      const isExcess = this.isExcessBenefit(benefit);

      if (isPVT || isExcess) {
        return '0.25% of vehicle value';
      }
      if (benefit.freeLimit > 0) {
        return `free up to Ksh ${Number(benefit.freeLimit).toLocaleString()}`;
      }
      if (benefit.divFactor === 1.0 || benefit.rateType === 'Amount') return 'flat rate';
      return `${benefit.rate}% of vehicle value`;
    },

    // ── Core premium calculation ──
    // vehicle: { value, limit } prices one vehicle of a multi-vehicle renewal instead of the single car.
    getFinalPremium(benefit, vehicle = null) {
      const isPVT = this.isPVTBenefit(benefit);
      const isExcess = this.isExcessBenefit(benefit);
      const carValue = vehicle ? vehicle.value : this.carValue;

      // For PVT/Excess Protector (percentage of vehicle value)
      if ((isPVT || isExcess) && carValue) {
        const val = typeof carValue === 'string'
          ? parseInt(carValue.replace(/\D/g, ''), 10)
          : Number(carValue);
        const rate = benefit.rate || 0.25;
        const amount = Math.ceil((rate / 100) * val);
        let min = 0;
        if (isPVT) min = 3000;
        else if (isExcess) min = 5000;
        return Math.max(amount, min);
      }

      // For freeLimit benefits: FREE unless a custom limit above freeLimit is set
      if (benefit.freeLimit > 0) {
        const custom = vehicle ? vehicle.limit : this.customLimits[benefit.sectCode];
        const freeLimit = Number(benefit.freeLimit) || 0;

        if (!custom || custom <= freeLimit) {
          return 0; // Free — no charge until agent sets a custom limit above freeLimit
        }

        // Charge rate% on the amount above the free limit only
        return Math.ceil((benefit.rate / benefit.divFactor) * (custom - freeLimit));
      }

      // For all other benefits: use premiumLookup (API-calculated value, summed across vehicles)
      if (this.premiumLookup && !vehicle) {
        const code = benefit.sectCode || benefit.sectionCode;
        const detail = this.premiumLookup[code];
        if (detail) return detail.benefitPremium || 0;
      }

      if (benefit.benefitPremium) return benefit.benefitPremium;
      if (benefit.rateType === 'Amount') return benefit.rate || 0;

      // Percentage fallback (no freeLimit, no API value)
      if (benefit.divFactor > 1 && carValue) {
        const v = typeof carValue === 'string'
          ? parseInt(carValue.replace(/\D/g, ''), 10)
          : Number(carValue);
        return Math.ceil((benefit.rate / benefit.divFactor) * (v || 0));
      }

      return 0;
    },

    getSurcharge(benefit) {
      // For freeLimit benefits: the charge IS the rate calculation above freeLimit
      // No separate surcharge concept applies
      if (benefit.freeLimit > 0) return 0;
      if (this.isExcessBenefit(benefit)) return 0;

      const custom = this.customLimits[benefit.sectCode];
      const freeLimit = Number(benefit.freeLimit) || 0;
      if (!custom || custom <= freeLimit) return 0;
      return Math.round((custom - freeLimit) * 0.1);
    },

    getFinalPremiumIncludingSurcharge(benefit) {
      return this.getFinalPremium(benefit) + this.getSurcharge(benefit);
    },

    getCustomLimitDisplay(benefit) {
      const val = this.customLimits[benefit.sectCode];
      return val ? Number(val).toLocaleString() : '';
    },

    toggleBenefit(benefit) {
      if (this.isLockedNoticeBenefit(benefit) && this.isSelected(benefit)) return;
      if (this.vehicleMode) {
        this.toggleAllVehicles(benefit);
        return;
      }
      const newSelected = [...this.selectedAddons];
      const idx = newSelected.indexOf(benefit.sectCode);

      if (idx > -1) {
        newSelected.splice(idx, 1);
        const newLimits = { ...this.customLimits };
        delete newLimits[benefit.sectCode];
        this.$emit(UPDATE_LIMITS_EVENT, newLimits);
      } else {
        newSelected.push(benefit.sectCode);
      }

      this.$emit('update:selectedAddons', newSelected);

      // ── Only trigger API sync if this is NOT a freeLimit benefit with no custom set ──
      const isFreeToggle = benefit.freeLimit > 0
        && (!this.customLimits[benefit.sectCode]
            || this.customLimits[benefit.sectCode] <= benefit.freeLimit);

      this.$emit('change', {
        selected: newSelected,
        limits: this.customLimits,
        skipApiSync: isFreeToggle,
      });
    },

    handleLimitInput(event, benefit) {
      const input = event.target;
      const raw = input.value.replace(/[^0-9]/g, '');
      const num = parseInt(raw, 10) || 0;
      const newLimits = { ...this.customLimits };

      if (num > 0) {
        newLimits[benefit.sectCode] = num;
      } else {
        delete newLimits[benefit.sectCode];
      }

      input.value = num ? Number(num).toLocaleString() : '';

      this.$emit(UPDATE_LIMITS_EVENT, newLimits);

      // ── Only trigger API if custom > freeLimit (or no freeLimit) ──
      const exceedsFreeLimit = !benefit.freeLimit || num > benefit.freeLimit;
      const min = this.minLimit(benefit);
      this.$emit('change', {
        selected: this.selectedAddons,
        limits: newLimits,
        // Free-cover limits are only synced once they are back at or above their minimum (underwritten or free) limit.
        skipApiSync: min > 0 ? num < min : !exceedsFreeLimit,
      });
    },

    handleLimitBlur(benefit) {
      if (!this.isBelowNoticeLimit(benefit)) return;
      const newLimits = { ...this.customLimits, [benefit.sectCode]: this.minLimit(benefit) };
      this.$emit(UPDATE_LIMITS_EVENT, newLimits);
      this.$emit('change', { selected: this.selectedAddons, limits: newLimits, skipApiSync: false });
    },

    lockedSection(benefit) {
      const section = this.noticeSection(benefit);
      return section && section.locked && section.limit > 0 ? section : null;
    },

    isLockedNoticeBenefit(benefit) {
      if (this.vehicleMode) {
        const holders = this.vehicles.filter((vehicle) => this.vehicleHas(benefit, vehicle));
        return holders.length > 0 && holders.every((vehicle) => this.isVehicleLocked(benefit, vehicle));
      }
      return !!(this.noticeSection(benefit) && this.noticeSection(benefit).locked);
    },

    isBelowNoticeLimit(benefit) {
      const min = this.minLimit(benefit);
      return min > 0 && (Number(this.customLimits[benefit.sectCode]) || 0) < min;
    },

    limitPlaceholder(benefit) {
      const section = this.lockedSection(benefit);
      if (section) return this.formatAmount(section.limit);
      return benefit.freeLimit ? `Free up to ${this.formatAmount(benefit.freeLimit)}` : 'Optional';
    },

    formatAmount(val) {
      return Number(val || 0).toLocaleString();
    },

    noticeSection(benefit) {
      if (this.vehicleMode) {
        return this.vehicles.map((vehicle) => this.vehicleSection(benefit, vehicle)).find(Boolean) || null;
      }
      if (!this.noticeSections) return null;
      return this.noticeSections[benefit.sectCode] || this.noticeSections[String(benefit.sectCode)] || null;
    },

    noticeLimitChange(benefit) {
      if (this.vehicleMode) {
        return this.vehicles.reduce((sum, vehicle) => sum + this.vehicleChange(benefit, vehicle), 0);
      }
      const section = this.noticeSection(benefit);
      if (!section || !this.isSelected(benefit)) return 0;
      const change = renewalLimitChangePremium(section, this.customLimits[benefit.sectCode]);
      return section.locked ? Math.max(change, 0) : change;
    },

    compactPrice(benefit) {
      if (this.vehicleMode) return this.vehicleModePrice(benefit);
      if (this.noticeSection(benefit)) {
        if (!this.isSelected(benefit)) return '';
        const change = this.noticeLimitChange(benefit);
        if (!change) return '';
        return `${change > 0 ? '+' : '−'} Ksh ${this.formatAmount(Math.abs(change))}`;
      }
      if (this.isFreeBenefit(benefit)) return 'Free';
      const amount = this.getFinalPremiumIncludingSurcharge(benefit);
      if (!amount) return '';
      return `Ksh ${this.formatAmount(amount)}`;
    },

    compactStatus(benefit) {
      if (this.vehicleMode) return this.vehicleModeStatus(benefit);
      if (this.noticeSection(benefit)) {
        const change = this.noticeLimitChange(benefit);
        if (change > 0) return 'Higher limit · added to renewal premium';
        if (change < 0) return 'Lower limit · taken off renewal premium';
        const locked = this.lockedSection(benefit);
        if (locked) return `Comes with this cover · free up to Ksh ${this.formatAmount(locked.limit)}`;
        if (this.isLockedNoticeBenefit(benefit)) return 'Comes with this cover';
        return 'Included in renewal premium';
      }
      if (this.isFreeBenefit(benefit)) return 'Included · no extra premium';
      return 'Included on this renewal';
    },

    signedAmount(amount) {
      if (!amount) return '';
      return `${amount > 0 ? '+' : '−'} Ksh ${this.formatAmount(Math.abs(amount))}`;
    },

    selectionOf(vehicle) {
      const selection = this.vehicleSelections[vehicle.key] || {};
      return { addons: selection.addons || [], limits: selection.limits || {} };
    },

    vehicleHas(benefit, vehicle) {
      return this.selectionOf(vehicle).addons.some((code) => String(code) === String(benefit.sectCode));
    },

    vehicleSection(benefit, vehicle) {
      const sections = vehicle.noticeSections || {};
      return sections[benefit.sectCode] || null;
    },

    vehicleLockedSection(benefit, vehicle) {
      const section = this.vehicleSection(benefit, vehicle);
      return section && section.locked && section.limit > 0 ? section : null;
    },

    isVehicleLocked(benefit, vehicle) {
      const section = this.vehicleSection(benefit, vehicle);
      return !!(section && section.locked && this.vehicleHas(benefit, vehicle));
    },

    vehicleLimit(benefit, vehicle) {
      return Number(this.selectionOf(vehicle).limits[benefit.sectCode]) || 0;
    },

    vehicleLimitDisplay(benefit, vehicle) {
      const limit = this.vehicleLimit(benefit, vehicle);
      return limit ? limit.toLocaleString() : '';
    },

    vehicleLimitPlaceholder(benefit, vehicle) {
      const section = this.vehicleLockedSection(benefit, vehicle);
      if (section) return this.formatAmount(section.limit);
      return benefit.freeLimit ? `Free up to ${this.formatAmount(benefit.freeLimit)}` : 'Optional';
    },

    isVehicleBelowNoticeLimit(benefit, vehicle) {
      const min = this.vehicleMinLimit(benefit, vehicle);
      return min > 0 && this.vehicleLimit(benefit, vehicle) < min;
    },

    // Change to the notice premium for a benefit this vehicle already had.
    vehicleLimitChange(benefit, vehicle) {
      const section = this.vehicleSection(benefit, vehicle);
      if (!section || !this.vehicleHas(benefit, vehicle)) return 0;
      const change = renewalLimitChangePremium(section, this.vehicleLimit(benefit, vehicle));
      return section.locked ? Math.max(change, 0) : change;
    },

    // Estimated cost of a benefit new to this vehicle; the synced quote premium is what gets charged.
    vehicleAddonPremium(benefit, vehicle) {
      if (!this.vehicleHas(benefit, vehicle) || this.vehicleSection(benefit, vehicle)) return 0;
      return this.getFinalPremium(benefit, { value: vehicle.value, limit: this.vehicleLimit(benefit, vehicle) });
    },

    vehicleChange(benefit, vehicle) {
      return this.vehicleLimitChange(benefit, vehicle) + this.vehicleAddonPremium(benefit, vehicle);
    },

    vehiclePrice(benefit, vehicle) {
      if (!this.vehicleHas(benefit, vehicle)) return '';
      const change = this.vehicleChange(benefit, vehicle);
      if (!change && !this.vehicleSection(benefit, vehicle) && benefit.freeLimit > 0) return 'Free';
      return this.signedAmount(change);
    },

    vehicleModePrice(benefit) {
      if (this.isSelected(benefit)) return this.signedAmount(this.noticeLimitChange(benefit));
      if (this.noticeSection(benefit)) return '';
      const estimate = this.vehicles.reduce(
        (sum, vehicle) => sum + this.getFinalPremium(benefit, { value: vehicle.value, limit: 0 }),
        0,
      );
      if (estimate) return `Ksh ${this.formatAmount(estimate)}`;
      return benefit.freeLimit > 0 ? 'Free' : '';
    },

    vehicleModeStatus(benefit) {
      const count = this.vehicles.filter((vehicle) => this.vehicleHas(benefit, vehicle)).length;
      const which = count === this.vehicles.length ? 'All vehicles' : `${count} of ${this.vehicles.length} vehicles`;
      const change = this.noticeLimitChange(benefit);
      if (change > 0) return `${which} · added to renewal premium`;
      if (change < 0) return `${which} · taken off renewal premium`;
      if (this.isLockedNoticeBenefit(benefit)) return `${which} · comes with this cover`;
      return `${which} · included on this renewal`;
    },

    emitVehicleSelections(selections, skipApiSync) {
      this.$emit('update:vehicleSelections', selections);
      this.$emit('change', { selections, skipApiSync });
    },

    withVehicle(vehicle, addons, limits) {
      return { ...this.vehicleSelections, [vehicle.key]: { addons, limits } };
    },

    toggleAllVehicles(benefit) {
      const on = this.isSelected(benefit);
      const code = String(benefit.sectCode);
      const next = { ...this.vehicleSelections };
      this.vehicles.forEach((vehicle) => {
        if (this.isVehicleLocked(benefit, vehicle)) return;
        const { addons, limits } = this.selectionOf(vehicle);
        const nextAddons = addons.filter((c) => String(c) !== code);
        const nextLimits = { ...limits };
        if (on) delete nextLimits[benefit.sectCode];
        else nextAddons.push(benefit.sectCode);
        next[vehicle.key] = { addons: nextAddons, limits: nextLimits };
      });
      const isFreeToggle = benefit.freeLimit > 0
        && this.vehicles.every((vehicle) => this.vehicleLimit(benefit, vehicle) <= benefit.freeLimit);
      this.emitVehicleSelections(next, isFreeToggle);
    },

    toggleVehicle(benefit, vehicle) {
      if (this.isVehicleLocked(benefit, vehicle)) return;
      const { addons, limits } = this.selectionOf(vehicle);
      const had = this.vehicleHas(benefit, vehicle);
      const nextAddons = had
        ? addons.filter((c) => String(c) !== String(benefit.sectCode))
        : [...addons, benefit.sectCode];
      const nextLimits = { ...limits };
      if (had) delete nextLimits[benefit.sectCode];
      const isFreeToggle = benefit.freeLimit > 0 && this.vehicleLimit(benefit, vehicle) <= benefit.freeLimit;
      this.emitVehicleSelections(this.withVehicle(vehicle, nextAddons, nextLimits), isFreeToggle);
    },

    handleVehicleLimitInput(event, benefit, vehicle) {
      const input = event.target;
      const num = parseInt(input.value.replace(/[^0-9]/g, ''), 10) || 0;
      const { addons, limits } = this.selectionOf(vehicle);
      const nextLimits = { ...limits };
      if (num > 0) nextLimits[benefit.sectCode] = num;
      else delete nextLimits[benefit.sectCode];
      input.value = num ? num.toLocaleString() : '';
      const min = this.vehicleMinLimit(benefit, vehicle);
      const exceedsFreeLimit = !benefit.freeLimit || num > benefit.freeLimit;
      this.emitVehicleSelections(
        this.withVehicle(vehicle, addons, nextLimits),
        min > 0 ? num < min : !exceedsFreeLimit,
      );
    },

    handleVehicleLimitBlur(benefit, vehicle) {
      if (!this.isVehicleBelowNoticeLimit(benefit, vehicle)) return;
      const { addons, limits } = this.selectionOf(vehicle);
      const nextLimits = { ...limits, [benefit.sectCode]: this.vehicleMinLimit(benefit, vehicle) };
      this.emitVehicleSelections(this.withVehicle(vehicle, addons, nextLimits), false);
    },
  },
};
</script>

<style scoped>
.benefit-selector-container {
  width: 100%;
}

.benefit-summary-strip {
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 174, 239, 0.06);
  border: 1px solid rgba(0, 174, 239, 0.2);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.78rem;
  color: #1E4276;
  font-weight: 500;
}

/* ── Expandable Benefit Cards ── */
.benefit-expand-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.bex-card {
  border: 1.5px solid #E8EDF2;
  border-radius: 10px;
  background: white;
  overflow: hidden;
  transition: border-color 0.15s;
}
.bex-card.bex-selected {
  border-color: #00AEEF;
  background: rgba(0, 174, 239, 0.03);
}
.bex-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
}
.bex-card:not(.bex-selected):hover .bex-head {
  background: #F8FDFF;
}
.bex-chk {
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid #CBD5E1;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s;
}
.bex-chk.bex-chk-on {
  background: #00AEEF;
  border-color: #00AEEF;
}
.bex-title-wrap {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.bex-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1E4276;
}
.bex-pricing-badge {
  font-size: 0.68rem;
  color: #0077aa;
  background: rgba(0, 174, 239, 0.09);
  border: 1px solid rgba(0, 174, 239, 0.2);
  border-radius: 20px;
  padding: 1px 8px;
  white-space: nowrap;
}
.bex-price {
  font-size: 0.75rem;
  font-weight: 600;
  color: #94a3b8;
  white-space: nowrap;
}
.bex-price-active {
  color: #10b981;
}
.bex-body {
  padding: 0 12px 12px;
  border-top: 1px solid #EBF8FF;
}
.bex-detail-grid {
  display: flex;
  flex-direction: column;
  padding-top: 4px;
}
.bex-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 0.75rem;
  border-bottom: 1px solid #F1F5F9;
}
.bex-row:last-child {
  border-bottom: none;
}
.bex-lbl {
  color: #64748b;
}
.bex-val {
  font-weight: 600;
  color: #1E4276;
}

.bex-surcharge-row {
  background: rgba(16, 185, 129, 0.04);
  margin: 0 -12px;
  padding: 6px 12px;
}
.bex-total-row {
  margin-top: 4px;
  padding-top: 10px;
  border-top: 1.5px solid #E2E8F0;
}
.underline-premium {
  text-decoration: underline;
  text-decoration-color: #00AEEF;
  text-underline-offset: 4px;
}

.bex-extra-wrap {
  background: #F8FAFC;
  border-radius: 8px;
  padding: 10px 12px;
  margin-top: 8px;
}
.bex-extra-lbl {
  font-size: 0.72rem;
  color: #64748b;
  font-weight: 500;
  display: block;
  margin-bottom: 6px;
}
.bex-extra-row {
  display: flex;
  align-items: center;
  border: 1.5px solid #E2E8F0;
  border-radius: 7px;
  background: white;
  overflow: hidden;
}
.bex-extra-prefix {
  padding: 8px 10px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #94a3b8;
  background: #F8FAFC;
  border-right: 1px solid #E2E8F0;
}
.bex-extra-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 8px 10px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #1E4276;
  background: white;
}
.bex-surcharge-chip {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 7px;
  font-size: 0.72rem;
  color: #0369a1;
  background: #EBF8FF;
  border: 1px solid #BAE6FD;
  border-radius: 7px;
  padding: 5px 9px;
}
.bex-surcharge-amt {
  font-weight: 700;
  color: #10b981;
  margin-left: auto;
}

/* ── Free benefit tag ── */
.bex-free-tag {
  display: flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 700;
  color: #059669;
  background: #F0FDF4;
  border: 1px solid #A7F3D0;
  border-radius: 20px;
  padding: 2px 8px;
  white-space: nowrap;
}

/* ── Free coverage notice in expanded body ── */
.bex-free-notice {
  display: flex;
  align-items: flex-start;
  background: #F0FDF4;
  border: 1px solid #A7F3D0;
  border-radius: 7px;
  padding: 8px 10px;
  font-size: 0.73rem;
  color: #065F46;
  margin-bottom: 6px;
  line-height: 1.5;
}

.fade-up-enter-active, .fade-up-leave-active {
  transition: all 0.2s ease;
}
.fade-up-enter, .fade-up-leave-to {
  opacity: 0;
  transform: translateY(5px);
}

.ben-compact {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.ben-kicker {
  margin: 0 0 8px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #94A3B8;
}
.ben-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ben-row,
.ben-row-main {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid #E8EDF2;
  border-radius: 12px;
  padding: 12px 14px;
  cursor: pointer;
}
.ben-row {
  flex-direction: column;
  align-items: stretch;
  gap: 0;
  padding: 0;
  overflow: hidden;
}
.ben-row-main {
  border: none;
  border-radius: 0;
  background: transparent;
}
.ben-row--on {
  border-color: #B8E8F9;
  background: #F7FCFF;
}
.ben-row:hover,
.ben-row-main:hover {
  border-color: #D6EAF8;
}
.ben-row-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}
.ben-row-note {
  font-size: 0.72rem;
  color: #64748B;
}
.ben-row-price {
  font-size: 0.8rem;
  font-weight: 700;
  color: #1E4276;
  white-space: nowrap;
}
.ben-row-main--locked {
  cursor: default;
}
.bex-chk-locked {
  background: #1E4276 !important;
  border-color: #1E4276 !important;
}
.ben-limit-hint {
  margin: 4px 0 0;
  font-size: 0.7rem;
  color: #B45309;
}
.ben-row-price--saving {
  color: #059669;
}
.ben-row-price--muted {
  color: #94A3B8;
  font-weight: 600;
}
.ben-off {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  background: #fff;
  border: 1px solid #E8EDF2;
  border-radius: 12px;
  padding: 12px 14px;
  cursor: pointer;
}
.ben-off:hover {
  border-color: #D6EAF8;
  background: #F8FDFF;
}
.ben-limit {
  padding: 0 14px 12px 42px;
}
.ben-cars {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 14px 12px 42px;
}
.ben-car {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ben-car-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
}
.ben-car-toggle--locked {
  cursor: default;
}
.ben-car-label {
  flex: 1;
  font-size: 0.78rem;
  font-weight: 600;
  color: #1E4276;
}
.ben-car-limit {
  padding-left: 26px;
}
.ben-limit-lbl {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748B;
  margin-bottom: 4px;
}

/* ── Free with comprehensive cover ── */
.ben-row--free {
  border-color: #A7F3D0;
  background: #F6FEF9;
  cursor: default;
}
.ben-row--free:hover {
  border-color: #A7F3D0;
}
.ben-free-main {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px 6px;
}
.ben-free-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  flex-shrink: 0;
}
.ben-link {
  align-self: flex-start;
  margin: 0 0 10px 42px;
  padding: 0;
  background: none;
  border: none;
  font-size: 0.72rem;
  font-weight: 600;
  color: #0077AA;
  cursor: pointer;
}
.ben-link:hover {
  text-decoration: underline;
}
.ben-link--flush {
  margin: 6px 0 0;
}
.ben-free-help {
  margin: 4px 0 0;
  font-size: 0.7rem;
  color: #64748B;
}
.ben-car-limit--free {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-left: 0;
}
.ben-car-limit--free .ben-car-label {
  flex: 0 0 90px;
}
.ben-car-limit--free .bex-extra-row {
  flex: 1;
}
.free-cover {
  border: 1px solid #A7F3D0;
  background: #F6FEF9;
  border-radius: 10px;
  padding: 12px;
}
.free-cover-head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #065F46;
}
.free-cover-hint {
  margin: 2px 0 8px 22px;
  font-size: 0.72rem;
  color: #047857;
}
.free-row {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #D1FAE5;
  border-radius: 8px;
  padding: 10px 12px;
}
.free-row + .free-row {
  margin-top: 6px;
}
.free-row-main {
  display: flex;
  align-items: center;
  gap: 10px;
}
.free-row-edit {
  margin-top: 8px;
}
</style>
